
// Nuxt / Vendor
import Vue, { VueConstructor } from "vue";

/*
 |------------------------------------------------------------------------------
 | Types
 |------------------------------------------------------------------------------
 */

export enum NodeType {
    undefined = "undefined",
    partial = "partial",
    page = "page",
    blog = "blog",
    testimonial = "testimonial"
}

/*
 |------------------------------------------------------------------------------
 | Generic meta types.
 |------------------------------------------------------------------------------
 */

export class HtmlHeadMeta {
    title?: string;
    meta: any[] = [];
    link: any[] = [];
    script: any[] = [];
}

export class ContentImageMeta {
    alt?: string;
    title?: string;
}

/*
 |------------------------------------------------------------------------------
 | Content module node type & interfaces.
 |------------------------------------------------------------------------------
 */

export class ContentPageComponents {
    header?: Array<string>;
    top?: Array<string>;
    bottom?: Array<string>;
    side?: Array<string>;
    footer?: Array<string>;
}

export class ContentNodePage {
    title?: string;
    headline?: string;
    layout?: string;
    classes?: Array<string>;
    components?: ContentPageComponents;
    image?: string;
    imageLink?: string;
    imageMeta?: ContentImageMeta;
    background?: string;
    backgroundTint?: boolean;
    backgroundPosition?: string;
}

/**
 * Schema dates eg: "2021-01-30T08:30:00+02:00"
 */
export class ContentNodeDates {
    created?: string;
    modified?: string;
    published?: string;
}

export class ContentNodeMetaFaq {
    question?: string;
    answer?: string;
}

export class ContentNodeMeta {
    noSubSchema?: boolean;
    title?: string;
    description?: string;
    robots?: string;
    sitemap?: boolean;
    changefreq?: string;
    priority?: string;
    faq?: Array<ContentNodeMetaFaq>
}

export interface SiblingLink {
    slug: string;
    title: string;
    path: string;
    extention: string;
}

export interface NodeSiblingLinks {
    prev: SiblingLink;
    next: SiblingLink;
}

export interface ContentNodeInterface {
    nodeType: NodeType;
    title: string;
    name: string;
    teaser?: string;
    published?: boolean;
    fullPath?: string;
    page?: ContentNodePage;
    metaData?: ContentNodeMeta;
    dates?: ContentNodeDates;
    sequence?: number;
    image?: string;
    siblingLinks?: NodeSiblingLinks;

    // Nuxt Content
    slug: string;
    toc: [];
    body: object;
    dir: string;
    path: string;
    createdAt: string;
    updatedAt: string;
}

export class ContentNode implements ContentNodeInterface {
    public nodeType: NodeType = NodeType.undefined;
    public title: string = "";
    public name: string = "";
    public teaser?: string;
    public published?: boolean = false;
    public fullPath?: string;
    public page: ContentNodePage = {};
    public metaData: ContentNodeMeta = {};
    public dates: ContentNodeDates = {};
    public sequence: number = 0;
    public image?: string;
    public siblingLinks?: NodeSiblingLinks;

    // Nuxt Content
    public slug: string = "";
    public toc: [] = [];
    public body: object = {};
    public dir: string = "";
    public path: string = "";
    public createdAt: string = "";
    public updatedAt: string = "";
}

/*
 |------------------------------------------------------------------------------
 | Partial - extends ContentNodeInterface
 |------------------------------------------------------------------------------
 */

export class PartialContentNode extends ContentNode {
    public nodeType = NodeType.partial;
}

/*
 |------------------------------------------------------------------------------
 | Pages - extends ContentNodeInterface
 |------------------------------------------------------------------------------
 */

export class PageContentNode extends ContentNode {
    public nodeType = NodeType.page;
}

/*
 |------------------------------------------------------------------------------
 | Blog - extends ContentNodeInterface
 |------------------------------------------------------------------------------
 */

// export class BlogContentNode extends ContentNode {
//     public nodeType = NodeType.blog;
// }

/*
 |------------------------------------------------------------------------------
 | Testimonials - extends ContentNodeInterface
 |------------------------------------------------------------------------------
 */

export interface TestimonialFieldOfStudy {
    primary?: string;
    secondary?: string;
}

export interface TestimonialNodeInterface extends ContentNodeInterface {
    field?: TestimonialFieldOfStudy;
}

export class TestimonialContentNode extends ContentNode implements TestimonialNodeInterface {
    public nodeType = NodeType.testimonial;
    public field?: TestimonialFieldOfStudy;
}

/*
 |------------------------------------------------------------------------------
 | Site data interfaces
 |------------------------------------------------------------------------------
 */

export class SiteDataImage {
    path: string = "";
    width: string|number = "";
    height: string|number = "";
}

export class SiteData {
    name?: string;
    title?: string;
    description?: string;
    siteUrl?: string;
    slogan?: string;
    locale?: string;
    country?: string;
    author?: string;
    altName?: string;
    siteLogo?: SiteDataImage;
}

/*
 |------------------------------------------------------------------------------
 | Injectables
 |------------------------------------------------------------------------------
 |
 | Typing used with provider/inject patter.
 |
 */

export interface GlobalInjectionInterface {
    node: ContentNode;
}

export const VueGlobalInjectable = Vue as VueConstructor<Vue & GlobalInjectionInterface>;

/*
 |------------------------------------------------------------------------------
 | Type Maps
 |------------------------------------------------------------------------------
 |
 | Handling for mapping different content types.
 |
 */

// export const NodeTypeMap = new Map([
//     [NodeType.partial, PartialContentNode],
//     [NodeType.page, PageContentNode],
//     [NodeType.blog, BlogContentNode],
//     [NodeType.testimonial, TestimonialContentNode]
// ]);

// -----------------------------------------------------------------------------
